/** EGB-00007 — Enterprise Release Governance Framework (mirrors pkg/releasegovernance) */

export const EGB_REL_ID = 'EGB-00007';

export interface CABMemberRow {
  role: string;
  responsibility: string;
  decisionRights: string;
  reviewCycle: string;
}

export interface ChangeManagementRow {
  area: string;
  policy: string;
  status: string;
  score: number;
}

export interface ChangeClassificationRow {
  class: string;
  count: number;
  status: string;
}

export interface ReleaseGovernanceRow {
  item: string;
  schedule: string;
  status: string;
}

export interface ReadinessGateRow {
  gate: string;
  status: string;
  score: number;
}

export interface ConfigurationRow {
  domain: string;
  control: string;
  status: string;
}

export interface DatabaseAIChangeRow {
  changeType: string;
  item: string;
  status: string;
  risk: string;
}

export interface ExecutiveDashboardRow {
  metric: string;
  value: string;
  status: string;
  score: number;
}

export const CAB_ROLES = [
  'Chief Technology Officer', 'Chief Enterprise Architect', 'Product Director', 'DevOps Director',
  'Security Director', 'QA Director', 'AI Governance Lead', 'Customer Success Director',
  'Operations Director', 'Accessibility Specialist',
];

export const CAB_FUNCTIONS = ['Roles', 'Responsibilities', 'Decision Rights', 'Approval Matrix', 'Risk Acceptance', 'Emergency Procedures'];

export const CHANGE_MANAGEMENT = [
  'Change Requests', 'Risk Classification', 'Business Impact', 'Architecture Review',
  'Dependency Analysis', 'Rollback Planning', 'Approval Workflow',
];

export const CHANGE_CLASSES = [
  'Standard Changes', 'Normal Changes', 'Major Changes', 'Emergency Changes',
  'AI Changes', 'Marketplace Changes', 'Infrastructure Changes', 'Database Changes',
];

export const RELEASE_GOVERNANCE = [
  'Release Planning', 'Release Calendar', 'Release Windows', 'Maintenance Windows',
  'Business Blackout Periods', 'Major Releases', 'Hotfixes',
];

export const RELEASE_READINESS = [
  'Architecture Approval', 'Testing Completion', 'Security Certification', 'Performance Certification',
  'Accessibility Certification', 'Documentation', 'Support Readiness', 'Rollback Validation',
];

export const CONFIGURATION_MANAGEMENT = [
  'Configuration Standards', 'Environment Variables', 'Feature Flags', 'Secrets', 'Certificates',
  'Marketplace Configuration', 'AI Configuration', 'Infrastructure Configuration',
];

export const DATABASE_AI_CHANGES = [
  'Database Migrations', 'Schema Reviews', 'Prompt Changes', 'DNA Updates',
  'Model Updates', 'Knowledge Updates', 'Rollback Procedures',
];

export const MARKETPLACE_INFRA = [
  'Marketplace API Changes', 'Connector Updates', 'Infrastructure Changes', 'Scaling', 'Networking', 'Monitoring',
];

export const ROLLBACK_RECOVERY = [
  'Application Rollback', 'Database Rollback', 'Prompt Rollback', 'Model Rollback',
  'Infrastructure Rollback', 'Configuration Rollback',
];

export const POST_RELEASE = [
  'Smoke Testing', 'Business Validation', 'Marketplace Validation', 'Performance Validation',
  'Customer Validation', 'Post Implementation Review',
];

export const CMDB = ['Application Inventory', 'Infrastructure Inventory', 'Dependencies', 'Ownership', 'Service Catalog', 'Configuration History'];

export const RELEASE_METRICS = ['Deployment Success', 'Rollback Frequency', 'Lead Time', 'Approval Time', 'Incident Rate', 'Operational Stability'];

export const EXECUTIVE_DASHBOARD = ['Release Health', 'Production Stability', 'Operational Readiness', 'Business Impact', 'Risk', 'Compliance'];

export const ACCESSIBILITY = ['Accessible Release Documentation', 'Accessible Dashboards', 'Inclusive Review Workflows'];

export const ARCHITECTURE_STAGES = [
  'Change Advisory Board', 'Change Management', 'Change Classification', 'Release Governance',
  'Release Readiness', 'Configuration Management', 'Database & AI Changes', 'Executive Dashboard',
];

export const LIVE_ROUTES: Record<string, string> = {
  masterConstitution: '/platform/master-constitution',
  enterpriseDevOps: '/platform/enterprise-devops',
  publishing: '/publishing',
  sre: '/sre',
};

export const DEMO_CAB: CABMemberRow[] = [
  { role: 'Chief Technology Officer', responsibility: 'Release strategy & production approval', decisionRights: 'Final release sign-off', reviewCycle: 'Weekly' },
  { role: 'DevOps Director', responsibility: 'Deployment pipelines & release windows', decisionRights: 'Release window approval', reviewCycle: 'Weekly' },
  { role: 'Security Director', responsibility: 'Security certification & change risk', decisionRights: 'Security gate veto', reviewCycle: 'Per release' },
  { role: 'QA Director', responsibility: 'Testing completion & quality gates', decisionRights: 'QA sign-off', reviewCycle: 'Per release' },
  { role: 'AI Governance Lead', responsibility: 'AI, prompt, and model change governance', decisionRights: 'AI change approval', reviewCycle: 'Per release' },
  { role: 'Operations Director', responsibility: 'Operational readiness & CMDB', decisionRights: 'Ops readiness sign-off', reviewCycle: 'Weekly' },
];

export const DEMO_CHANGE_MANAGEMENT: ChangeManagementRow[] = [
  { area: 'Change Requests', policy: 'All changes via governed workflow', status: 'enforced', score: 100 },
  { area: 'Risk Classification', policy: 'Standard · Normal · Major · Emergency', status: 'enforced', score: 98 },
  { area: 'Rollback Planning', policy: 'Mandatory rollback plan per change', status: 'required', score: 95 },
  { area: 'Approval Workflow', policy: 'CAB approval for major changes', status: 'active', score: 97 },
];

export const DEMO_CHANGE_CLASSIFICATION: ChangeClassificationRow[] = [
  { class: 'Standard Changes', count: 142, status: 'automated' },
  { class: 'Normal Changes', count: 38, status: 'approved' },
  { class: 'Major Changes', count: 6, status: 'cab_review' },
  { class: 'AI Changes', count: 12, status: 'ai_governance' },
  { class: 'Marketplace Changes', count: 8, status: 'validated' },
  { class: 'Database Changes', count: 4, status: 'schema_review' },
];

export const DEMO_RELEASE_GOVERNANCE: ReleaseGovernanceRow[] = [
  { item: 'Q3 Release Calendar', schedule: '6 releases planned', status: 'on_track' },
  { item: 'Release Window', schedule: 'Tue/Thu 02:00–06:00 UTC', status: 'active' },
  { item: 'Maintenance Window', schedule: 'Sun 01:00–04:00 UTC', status: 'scheduled' },
  { item: 'Business Blackout', schedule: 'Prime Day · BFCM', status: 'blocked' },
  { item: 'Next Major Release', schedule: 'v0.35.0 · Aug 15', status: 'planning' },
];

export const DEMO_RELEASE_READINESS: ReadinessGateRow[] = [
  { gate: 'Architecture Approval', status: 'approved', score: 100 },
  { gate: 'Testing Completion', status: 'passing', score: 98 },
  { gate: 'Security Certification', status: 'certified', score: 97 },
  { gate: 'Performance Certification', status: 'validated', score: 95 },
  { gate: 'Accessibility Certification', status: 'validated', score: 94 },
  { gate: 'Rollback Validation', status: 'tested', score: 96 },
];

export const DEMO_CONFIGURATION: ConfigurationRow[] = [
  { domain: 'Configuration', control: 'Environment variable standards', status: 'enforced' },
  { domain: 'Configuration', control: 'Feature flag governance', status: 'active' },
  { domain: 'Configuration', control: 'Secrets via vault only', status: 'enforced' },
  { domain: 'Marketplace', control: 'SP-API config versioning', status: 'tracked' },
  { domain: 'AI', control: 'Prompt & model config registry', status: 'active' },
];

export const DEMO_DATABASE_AI: DatabaseAIChangeRow[] = [
  { changeType: 'Database', item: '002_fcp00008_decision_intelligence', status: 'approved', risk: 'low' },
  { changeType: 'Database', item: 'Schema review pending · orders index', status: 'review', risk: 'medium' },
  { changeType: 'AI', item: 'Prompt DNA v2.4 rollout', status: 'cab_scheduled', risk: 'medium' },
  { changeType: 'AI', item: 'Model routing config update', status: 'approved', risk: 'low' },
  { changeType: 'Knowledge', item: 'Brand knowledge graph sync', status: 'deployed', risk: 'low' },
];

export const DEMO_EXECUTIVE: ExecutiveDashboardRow[] = [
  { metric: 'Release Health', value: 'A', status: 'healthy', score: 92 },
  { metric: 'Production Stability', value: '99.7% uptime', status: 'stable', score: 99 },
  { metric: 'Deployment Success', value: '96%', status: 'healthy', score: 96 },
  { metric: 'Rollback Frequency', value: '2 this quarter', status: 'low', score: 88 },
  { metric: 'Lead Time', value: '4.2 days', status: 'improving', score: 85 },
  { metric: 'Operational Readiness', value: 'Certified', status: 'ready', score: 94 },
];

export function buildReleaseDashboard() {
  return {
    council_roles: CAB_ROLES.length,
    change_classes: CHANGE_CLASSES.length,
    readiness_gates: RELEASE_READINESS.length,
    configuration_areas: CONFIGURATION_MANAGEMENT.length,
    governance_areas: CHANGE_MANAGEMENT.length + POST_RELEASE.length,
  };
}
